import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import { useAuthStore } from '../../../stores/auth.store';
import { useModalStore } from '../../../stores/modal.store';
import { DesktopOnly } from '../../../components/ui/DesktopOnly';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Icon } from '../../../components/ui/Icon';
import { SectionMark } from '../../../components/ui/SectionMark';
import { ActionsMenu, type ActionItem } from '../../../components/ui/ActionsMenu';
import { useUrlState } from '../../../hooks/useUrlState';
import { fmtEur } from '../../../lib/utils';
import { FATTURE_VISTE, type FattureVista } from '../../../lib/adminRoutes';
import { canReadAdmin, canWriteAdmin } from '../adminApi';
import { NewDocumentButton } from '../../invoices/DocumentsSection';
import { AdminHeader, StoricoNote, useStorico, useVista } from '../shared/AdminShell';
import { IncassiTab } from '../IncassiTab';
import { ChiCiDeve, raggruppaPerCliente } from './ChiCiDeve';
import { EntrateFisse } from './EntrateFisse';
import { FattureArchivio } from './FattureArchivio';
import { FattureMobile } from './FattureMobile';
import { daIncassareQuery, durata, nomeBreve, quando, type DaIncassare } from './fattureApi';
import { useIsMobile } from '../../../hooks/useIsMobile';
import '../admin.css';
import './fatture.css';

/**
 * Fatture (`/admin/fatture`), ridisegnata il 26/10/2026 (os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: chi ci deve soldi e da quanto? E cosa ci entra ogni mese dai canoni?
 * La forma: una pagina sola che si legge scendendo. In cima la risposta in una frase; poi i
 * clienti che ci devono soldi (logo o iniziali, quanto e da quanto, le rate coi gesti sulla
 * riga); poi le entrate fisse, i canoni che fatturiamo a mano (prima stavano in Abbonamenti).
 *
 * Alleggerita ("troppe cose inutili", Lorenzo 26/10): via le tre linguette, la fascia dei tre
 * numeri, la barra di ricerca, ordina e filtra. Le altre liste ci sono ancora e si aprono dal
 * menu ⋯ o dai due link in fondo, con lo stesso indirizzo di prima (`?vista=`, `?status=`):
 *   Tutte le fatture emesse   ?vista=documenti   (FattureArchivio, per mese)
 *   Preventivi                ?vista=preventivi  (FattureArchivio)
 *   Fatture già incassate     ?status=incassate  (IncassiTab, com'era)
 *   Fatture tolte dall'elenco ?status=cestinate  (IncassiTab, com'era)
 * Permessi: "chi ci deve" e le entrate fisse sono dell'Amministrazione, l'archivio della
 * Fatturazione. Sul telefono la pagina è FattureMobile.
 */
function livello(user: { permissions: Array<{ module: string; level: string }> } | null | undefined, mod: string): PermissionLevel {
  return (user?.permissions.find((p) => p.module === mod)?.level ?? PermissionLevel.NONE) as PermissionLevel;
}

export function FatturePage() {
  const isMobile = useIsMobile();
  return isMobile ? <FattureMobile /> : <FattureDesktop />;
}

function FattureDesktop() {
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  const canAdmin = canReadAdmin(user);
  const canWrite = canWriteAdmin(user);
  const lvDocs = livello(user, ModuleKey.FATTURAZIONE);
  const canReadDocs = hasAtLeast(lvDocs, PermissionLevel.READ);
  // Emettere documenti è della Fatturazione, non dell'Amministrazione
  const canWriteDocs = hasAtLeast(lvDocs, PermissionLevel.WRITE);

  const permesse = FATTURE_VISTE.filter((k) => (k === 'incassare' ? canAdmin : canReadDocs));
  const [vista, setVista] = useVista<FattureVista>(permesse.length ? permesse : FATTURE_VISTE);
  const [status, setStatus] = useUrlState<string>('status', 'aperte');
  const storico = useStorico();
  const { data } = useQuery({ ...daIncassareQuery(storico.on), enabled: canAdmin && vista === 'incassare' });

  if (!permesse.length) {
    return (
      <div className="page adm-page">
        <AdminHeader title="Fatture" />
        <EmptyState brand title="Le fatture non sono per il tuo profilo" description="Per vederle serve l'accesso all'Amministrazione o alla Fatturazione: chiedilo a chi gestisce gli utenti." />
      </div>
    );
  }

  const altraLista = vista === 'incassare' && (status === 'incassate' || status === 'cestinate');
  const tornaIndietro = () => { setStatus('aperte'); if (vista !== 'incassare') setVista('incassare'); };

  // Le azioni rare: tutte nel menu ⋯ (prima erano linguette, bottoni e menu nella barra).
  const menu: ActionItem[] = [
    { key: 'contanti', label: 'Registra un incasso in contanti', icon: 'banknote', onClick: () => openModal('newCashEntry', { direction: 'IN' }), hidden: !canWrite },
    { key: 'documenti', label: 'Tutte le fatture emesse da noi', icon: 'invoice', onClick: () => setVista('documenti'), hidden: !canReadDocs || vista === 'documenti' },
    { key: 'preventivi', label: 'I preventivi', icon: 'file', onClick: () => setVista('preventivi'), hidden: !canReadDocs || vista === 'preventivi' },
    { key: 'incassate', label: 'Le fatture già incassate', icon: 'check', onClick: () => { setVista('incassare'); setStatus('incassate'); }, hidden: !canAdmin },
    { key: 'cestinate', label: "Le fatture tolte dall'elenco", icon: 'trash', onClick: () => { setVista('incassare'); setStatus('cestinate'); }, hidden: !canAdmin },
    { ...storico.menuItem },
  ];

  const titoloVista = vista === 'documenti' ? 'Tutte le fatture emesse da noi' : vista === 'preventivi' ? 'I preventivi' : null;

  return (
    <div className="page adm-page ft-page">
      {/* ── La risposta ──────────────────────────────────────────────── */}
      <header className="ft-hd">
        <div className="ft-hd-main">
          <SectionMark icon="invoice" className="ft-sez" />
          <div className="min-w-0">
            <h1 className="ft-title">Fatture</h1>
            {titoloVista || altraLista ? (
              <p className="ft-risposta">
                <a className="ft-indietro" onClick={tornaIndietro}><Icon name="arrowLeft" size={12} /> Chi ci deve soldi</a>
                <span className="ft-dove">{titoloVista ?? (status === 'incassate' ? 'Le fatture già incassate' : "Le fatture tolte dall'elenco")}</span>
              </p>
            ) : (
              <p className="ft-risposta">{canAdmin ? (data ? risposta(data) : 'Sto guardando chi ci deve soldi…') : 'Le fatture emesse da noi e i preventivi.'}</p>
            )}
          </div>
        </div>
        <div className="ft-hd-azioni">
          {canWriteDocs && <NewDocumentButton />}
          <ActionsMenu variant="ghost" label="Altre liste e azioni" items={menu} />
        </div>
      </header>

      {storico.on && <StoricoNote year={storico.year} onOff={() => storico.set(false)} />}

      {vista === 'incassare' && canAdmin && (altraLista ? (
        <IncassiTab status={status as 'incassate' | 'cestinate'} canWrite={canWrite} showHistory={storico.on} onBack={() => setStatus('aperte')} />
      ) : (
        <>
          <section className="ft-blocco" aria-label="Chi ci deve soldi">
            <h2 className="ft-h2"><Icon name="user" size={15} /> Chi ci deve soldi</h2>
            {data ? <ChiCiDeve data={data} canWrite={canWrite} canReadDocs={canReadDocs} /> : <p className="ft-vuoto">Carico le fatture da incassare…</p>}
          </section>

          <EntrateFisse />

          {canReadDocs && (
            <p className="ft-altre">
              Cerchi una fattura già pagata o un preventivo?
              <a onClick={() => setVista('documenti')}>Tutte le fatture emesse <Icon name="chevronR" size={10} /></a>
              <a onClick={() => setVista('preventivi')}>Preventivi <Icon name="chevronR" size={10} /></a>
            </p>
          )}
        </>
      ))}
      {vista === 'documenti' && canReadDocs && <FattureArchivio tipo="fatture" showHistory={storico.on} />}
      {vista === 'preventivi' && canReadDocs && <FattureArchivio tipo="preventivi" showHistory={storico.on} />}

      {/* Sul telefono creare un documento resta al computer (procedura lunga): lo dice una riga. */}
      {canWriteDocs && vista !== 'incassare' && (
        <DesktopOnly
          cosa="La creazione di fatture e preventivi"
          perche="è una procedura a più passaggi con righe, imposte e scadenze; qui puoi consultare le fatture che ci sono e segnarle incassate."
        >
          <div className="m-actionbar m-only">
            <NewDocumentButton />
          </div>
        </DesktopOnly>
      )}
    </div>
  );
}

/**
 * La risposta in una frase, calcolata dai dati. L'importo compare solo quando i clienti sono più
 * d'uno: con un cliente solo il suo totale sta già sulla sua riga (ogni numero una volta sola).
 */
function risposta(d: DaIncassare): ReactNode {
  const clienti = raggruppaPerCliente(d.items);
  if (!clienti.length) return <>Nessun cliente ci deve soldi: tutte le fatture emesse risultano incassate.</>;
  const tardi = clienti.filter((c) => c.ritardo > 0);
  const totale = d.items.reduce((s, r) => s + r.amount, 0);
  const banca = d.items.filter((r) => r.bonifico).length;
  const primo = clienti[0]!;
  const conBanca = banca > 0
    ? <> Per {banca === 1 ? 'una rata' : `${banca} rate`} c'è già il bonifico in banca: basta confermarlo.</>
    : null;

  if (clienti.length === 1) {
    const n = primo.rate.length;
    const rate = n === 1 ? 'una rata' : `${n} rate`;
    return (
      <>Solo <strong>{nomeBreve(primo.nome)}</strong> ci deve dei soldi: {rate}
        {primo.ritardo > 0 ? <>, <span className="is-alert">in ritardo da {durata(primo.ritardo)}</span>.</> : <>, la prima scade {quando(primo.rate[0]?.dueDate)}.</>}
        {conBanca}
      </>
    );
  }
  return (
    <>
      <strong>{clienti.length} clienti</strong> ci devono <strong>€ {fmtEur(totale)}</strong>
      {tardi.length > 0
        ? <>: {tardi.length === 1 ? 'uno è' : `${tardi.length} sono`} in ritardo, <span className="is-alert">{nomeBreve(primo.nome)} da {durata(primo.ritardo)}</span>.</>
        : <>, nessuno in ritardo.</>}
      {conBanca}
    </>
  );
}
